import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle, FileText, Upload } from 'lucide-react';

export default function PublicFormPage() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadForm = async () => {
      try {
        const res = await publicAPI.getForm(token);
        if (res.data.status === 'submitted') {
          setSubmitted(true);
          setForm(res.data);
        } else {
          setForm(res.data);
          const initial = {};
          res.data.fields?.forEach(f => {
            if (f.type === 'checkbox') initial[f.name] = false;
            else if (f.type === 'readonly' || f.type === 'readonly_long') initial[f.name] = f.value || '';
            else if (f.type === 'file') initial[f.name] = '';
            else initial[f.name] = '';
          });
          setResponses(initial);
        }
      } catch {
        toast.error('Form not found');
      } finally {
        setLoading(false);
      }
    };
    loadForm();
  }, [token]);

  const handleFileUpload = async (fieldName, file) => {
    setFileUploads(prev => ({ ...prev, [fieldName]: file.name }));
    setUploading(true);
    try {
      const res = await publicAPI.uploadFormFile(token, file);
      setResponses(prev => ({
        ...prev,
        [fieldName]: res.data.file_url,
        [`${fieldName}_storage_type`]: res.data.storage_type
      }));
      toast.success('File uploaded successfully');
    } catch {
      toast.error('Failed to upload file');
      setFileUploads(prev => ({ ...prev, [fieldName]: null }));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = form.fields?.filter(f => f.required && f.type !== 'readonly' && f.type !== 'readonly_long' && !responses[f.name]);
    if (missing?.length) {
      toast.error(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      await publicAPI.submitForm(token, responses);
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#2F4F4F]" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center">
        <CardContent className="pt-8 pb-8">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Thank You!</h2>
          <p className="text-muted-foreground">{form?.message || 'Your form has been submitted successfully.'}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (!form) return (
    <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center">
      <p className="text-muted-foreground">Form not found.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f0e8] py-8 px-4" data-testid="public-form-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#2F4F4F]">U-DORM</h1>
          <p className="text-sm text-muted-foreground">Dormitory Management System</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C85A42]" />
              {form.title}
            </CardTitle>
            {form.tenant_name && (
              <div className="mt-2 p-3 rounded-lg bg-[#2F4F4F]/5 border border-[#2F4F4F]/10">
                <p className="text-sm text-[#2F4F4F] font-medium">
                  This form is for: <span className="font-bold">{form.tenant_name}</span>
                </p>
              </div>
            )}
            {form.description && <p className="text-sm text-muted-foreground mt-2">{form.description}</p>}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {form.fields?.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </Label>

                  {field.type === 'text' && (
                    <Input
                      id={field.name}
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'email' && (
                    <Input
                      id={field.name}
                      type="email"
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'date' && (
                    <Input
                      id={field.name}
                      type="date"
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'textarea' && (
                    <Textarea
                      id={field.name}
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      rows={3}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'checkbox' && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={field.name}
                        checked={responses[field.name] || false}
                        onCheckedChange={(v) => setResponses({ ...responses, [field.name]: v })}
                        data-testid={`form-field-${field.name}`}
                      />
                      <Label htmlFor={field.name} className="text-sm font-normal">{field.label}</Label>
                    </div>
                  )}

                  {field.type === 'select' && (
                    <Select
                      value={responses[field.name] || ''}
                      onValueChange={(v) => setResponses({ ...responses, [field.name]: v })}
                    >
                      <SelectTrigger data-testid={`form-field-${field.name}`}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {field.type === 'number' && (
                    <Input
                      id={field.name}
                      type="number"
                      step="0.01"
                      min="0"
                      value={responses[field.name] || ''}
                      onChange={(e) => setResponses({ ...responses, [field.name]: e.target.value })}
                      required={field.required}
                      placeholder={field.placeholder || ''}
                      data-testid={`form-field-${field.name}`}
                    />
                  )}

                  {field.type === 'file' && (
                    <div className="space-y-2">
                      <label
                        className="flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                        data-testid={`form-field-${field.name}`}
                      >
                        <Upload className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{fileUploads[field.name] || 'Click to upload file'}</p>
                          <p className="text-xs text-muted-foreground">{field.accept || 'Image or PDF'}</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept={field.accept || 'image/*,.pdf'}
                          onChange={(e) => e.target.files[0] && handleFileUpload(field.name, e.target.files[0])}
                        />
                      </label>
                      {uploading && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</p>}
                      {responses[field.name] && !uploading && <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> File uploaded</p>}
                    </div>
                  )}

                  {field.type === 'readonly' && (
                    <div className="p-3 bg-muted rounded-lg text-sm font-medium" data-testid={`form-field-${field.name}`}>
                      {field.value || responses[field.name] || 'N/A'}
                    </div>
                  )}

                  {field.type === 'readonly_long' && (
                    <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid={`form-field-${field.name}`}>
                      {field.value || responses[field.name] || 'No terms provided.'}
                    </div>
                  )}
                </div>
              ))}

              <Button
                type="submit"
                className="w-full bg-[#C85A42] hover:bg-[#B54A35]"
                disabled={submitting}
                data-testid="submit-form-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submit Form
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
